import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, EVENT_TYPES, IMG } from "../data/site";
import { FadeUp, SectionHead } from "./Motion";

const EMPTY = { name: "", email: "", phone: "", event_type: "Wedding Catering", event_date: "", guests: "", message: "" };

const Catering = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const applyPkg = () => {
      const pkg = localStorage.getItem("sde_package");
      if (pkg) {
        setForm((f) => ({ ...f, message: `Package enquiry: ${pkg}. ` }));
        localStorage.removeItem("sde_package");
      }
    };
    applyPkg();
    window.addEventListener("hashchange", applyPkg);
    return () => window.removeEventListener("hashchange", applyPkg);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        type: "catering",
        guests: form.guests ? parseInt(form.guests, 10) : null,
      });
      toast.success("Enquiry received — our catering team will call you within 24 hours.");
      setForm(EMPTY);
    } catch {
      toast.error(`Something went wrong. Please call us at 732-318-6284.`);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/12 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <section id="catering" data-testid="catering-section" className="relative overflow-hidden border-y border-white/8">
      <div className="absolute inset-0">
        <img
          src={IMG.interiorGenerated}
          onError={(e) => { e.currentTarget.src = IMG.cateringBanquet; }}
          alt=""
          className="h-full w-full object-cover opacity-30"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 py-32 md:px-10 md:py-40">
        <SectionHead
          index="06"
          kicker="Premium Catering"
          title={<>Your celebration, <em className="italic text-gold">our kitchen</em></>}
        />

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <p className="max-w-lg text-lg leading-relaxed text-white/70">
                Live dosa counters, banana-leaf feasts and bespoke vegetarian menus — executed
                with the same care we bring to every plate in our restaurant.
              </p>
            </FadeUp>
            <FadeUp delay={0.12} className="mt-10 flex flex-wrap gap-3" >
              {EVENT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`event-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setForm({ ...form, event_type: t })}
                  className={`rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                    form.event_type === t
                      ? "border-gold bg-gold/15 text-gold"
                      : "border-white/15 text-white/60 hover:border-gold/60 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </FadeUp>
            <FadeUp delay={0.2} className="mt-12 overflow-hidden rounded-2xl border border-white/10">
              <img
                src={IMG.cateringFeast}
                alt="Festive South Indian banana-leaf feast"
                loading="lazy"
                className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <form onSubmit={submit} data-testid="catering-form" className="glass rounded-2xl p-7 md:p-9">
              <h3 className="font-serif text-3xl">Book Catering</h3>
              <p className="mt-2 text-sm text-mist">Tell us about your event — we respond within 24 hours.</p>
              <div className="mt-7 space-y-4">
                <input required data-testid="catering-name-input" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <input required type="email" data-testid="catering-email-input" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
                  <input required data-testid="catering-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <input type="date" data-testid="catering-date-input" value={form.event_date} onChange={set("event_date")} className={`${inputCls} [color-scheme:dark]`} />
                  <input type="number" min="1" data-testid="catering-guests-input" value={form.guests} onChange={set("guests")} placeholder="Guests (approx.)" className={inputCls} />
                </div>
                <textarea data-testid="catering-message-input" value={form.message} onChange={set("message")} placeholder="Venue, menu wishes, dietary needs…" rows={3} className={inputCls} />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="catering-submit-button"
                  className="btn-gold w-full rounded-full py-4 text-[13px] font-semibold uppercase tracking-[0.22em] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Request a Callback"}
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Catering;
